import {api} from "@/shared/api";

import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {AddisonEAUConfigDialog} from "./addisonEAUConfigDialog";
import {PanelBase} from "./panelBase";
import {Integrations} from "../integrations";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {ExportType, IncapacityReason, IntegrationType, RecordedEntryStatus} from "@/shared/timrTypes";

export type IncapacityReasonMap = Map<number, IncapacityReason>;
type IntegrationConfiguration = {
	clientNr: number,
	incapacityReasonMap: Record<string, IncapacityReason>,
	statusAfterExport: RecordedEntryStatus,
	dateFrom: string
};

export interface AddisonEAUConfig {
	clientNr: number;
	incapacityReasonMap: IncapacityReasonMap;
	statusAfterExport: RecordedEntryStatus;
	dateFrom: Temporal.PlainDate | null;
}

export class AddisonEAU extends PanelBase {

	constructor() {
		super();
	}

	// Called when the element is inserted in the DOM
	// DEVNOTE: No shadow DOM here, so we can use external css
	private connectedCallback() {
		this.innerHTML = this.renderPanel({
			logoHtml: `<img src="/timr/images/integrations/addison.svg?version=1" alt="ADDISON Export" height="36px">`,
			topActionsHtml: this.renderEditActionButton(),
			footerHtml: `
				<button type="button" id="exportNow" class="btn btn-primary mt-10 mb-0 mr-10">
					<i class="far fa-file-export position-left" aria-hidden="true"></i>${messages["integrations.addisonEAU.exportNow"]}
				</button>
			`
		});
		this.initListeners();
	}

	private initListeners() {
		$(this).find("#edit").on("click", () => {
			AddisonEAUConfigDialog.edit(this._integration, this.mapConfigurationToAddisonConfig(this._integration.configuration as IntegrationConfiguration));
		});
		this.querySelector("[data-delete-integration]").addEventListener("click", () => {
			BootstrapModal.confirm({
				title: messages["dialog.delete.confirm"],
				message: messages["integrations.addisonEAU.delete.message"],
				yesAction: (confirmModal) => {
					confirmModal.close();
					Integrations.triggerDeletedIntegrationEvent(IntegrationType.ADDISON, this.integration.id);
				}
			});
		});
		$(this).find("#exportNow").on("click", (e) => {
			Button.setLoading($(e.target));
			this.startExport();
		});
	}

	protected updateBanner(): void {
		// Nothing to do here
	}

	private startExport(): void {
		void api("integrations/addison/findUsersWithoutEmployeeNr.json")
			.get({id: this._integration.id})
			.done((users: { fullName: string }[]) => {
				if (users.length > 0) {

					let message = users
						.map((user) => messages.withArguments("integrations.addisonEAU.missingEmployeeNr.name", user.fullName))
						.join("<br>");

					if (users.length > 1) {
						message += "<br><br>" + messages["integrations.addisonEAU.missingEmployeeNr.multiple"];
					} else {
						message += "<br><br>" + messages["integrations.addisonEAU.missingEmployeeNr.single"];
					}

					BootstrapModal.confirm({
						message: message,
						escapeText: false,
						preventDoubleClickButtons: true,
						yesLabel: messages["integrations.addisonEAU.createExport"],
						yesCss: "btn-primary",
						noCss: "btn-default",
						yesAction: (m) => {
							this.export();
							m.close();
						},
						noAction: (m) => {
							Button.resetLoading($(this).find("#exportNow"));
							m.close();
						}
					});
				} else {
					this.export();
				}
			})
			.fail(() => Button.resetLoading($(this).find("#exportNow")));
	}

	private export() {
		void api("integrations/addison/export.json")
			.post({id: this._integration.id})
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportType: ExportType.Csv,
						allowCancellation: true,
						loadingMessage: messages["downloadStatus.loadingMessage.default"]
					});
				}
			})
			.always(() => Button.resetLoading($(this).find("#exportNow")));
	}

	private mapConfigurationToAddisonConfig(configuration: IntegrationConfiguration): AddisonEAUConfig {
		return {
			clientNr: configuration.clientNr,
			incapacityReasonMap: this.objToMap(configuration.incapacityReasonMap),
			statusAfterExport: configuration.statusAfterExport,
			dateFrom: parseISODate(configuration.dateFrom)
		};
	}

	private objToMap(obj: Record<string, IncapacityReason>): IncapacityReasonMap {
		const map: IncapacityReasonMap = new Map();
		Object.keys(obj).forEach((key) => {
			map.set(Number(key), obj[key]);
		});
		return map;
	}
}
